import { exists, sortComparatorAsc } from 'base/helpers';
import { ElementType, LibraryElement, SlotType } from 'base/library_service';
import blankSectionImage from './category_images/blank.file.svg';
import buttonRowImage from './element_type_images/button-row.file.svg';
import buttonImage from './element_type_images/button.file.svg';
import headingRowImage from './element_type_images/heading-row.file.svg';
import headingImage from './element_type_images/heading.file.svg';
import iconImage from './element_type_images/icon.file.svg';
import imageImage from './element_type_images/image.file.svg';
import listImage from './element_type_images/list.file.svg';
import paragraphImage from './element_type_images/paragraph.file.svg';
import textImage from './element_type_images/text.file.svg';
import videoImage from './element_type_images/video.file.svg';

type SlotAddPanelOption = {
  elementId: string;
  label: string;
  image: string;
};

export const getSlotAddPanelOptions = ({
  slotType,
  elementTypes = [],
}: {
  slotType: SlotType;
  elementTypes?: ElementType[]; // optional because it's only used for sorting at the moment
}): SlotAddPanelOption[] => {
  const allowedElementsByElementTypeId = new Map<string, LibraryElement[]>();
  const elementTypeNamesById = new Map<string, string>();

  for (const el of slotType.allowedElements) {
    if (el.elementType === null) continue;

    const elements =
      allowedElementsByElementTypeId.get(el.elementType.id) ?? [];
    elements.push(el);
    allowedElementsByElementTypeId.set(el.elementType.id, elements);

    elementTypeNamesById.set(el.elementType.id, el.elementType.name);
  }

  return [...allowedElementsByElementTypeId]
    .sort(
      sortComparatorAsc(([elementTypeId]) =>
        elementTypes.findIndex(({ id }) => id === elementTypeId),
      ),
    )
    .map(([elementTypeId, elements]) => {
      const defaultElementId = defaultElementIdForElementType(elementTypeId);

      const el =
        (defaultElementId !== undefined
          ? elements.find(({ id }) => id === defaultElementId)
          : undefined) ?? elements[0];
      if (el === undefined) return null;

      const label = elementTypeNamesById.get(elementTypeId);
      if (label === undefined) return null;

      return {
        elementId: el.id,
        label,
        image: imageForElementType(elementTypeId),
      };
    })
    .filter(exists);
};

const defaultElementIdForElementType = (elementTypeId: string) => {
  // TODO: do default elements in a more robust way. For now, we're just
  // hardcoding nice defaults for a few element types, with fallback to
  // the first allowed element if not found.
  switch (elementTypeId) {
    case 'ET1': // Button
      return 'EL13'; // Button Primary
    case 'ET2': // Heading
      return 'EL3'; // H2
    case 'ET3': // Text
      return 'EL10'; // Paragraph Regular
    case 'ET4': // Icon
      return 'EL44'; // Icon medium
    default:
      return undefined;
  }
};

const imageForElementType = (elementTypeId: string) => {
  switch (elementTypeId) {
    case 'ET1': // Button
      return buttonImage;
    case 'ET2': // Heading
      return headingImage;
    case 'ET3': // Paragraph
      return paragraphImage;
    case 'ET4': // Icon
      return iconImage;
    case 'ET6': // Image
      return imageImage;
    case 'ET7': // Video
      return videoImage;
    case 'ET8': // List
      return listImage;
    case 'ET9': // Button Group
      return buttonRowImage;
    case 'ET10': // Heading Group
      return headingRowImage;
    case 'ET11': // Text
      return textImage;
    default:
      return blankSectionImage;
  }
};
